import { css } from '@emotion/css'
import { EventPosterImage, hasCurrentRenderedPoster, hasRenderedPoster, usesRenderedPoster } from 'Shared/frontend/eventPosters/EventPosterImage';
import { EventPoster } from 'Shared/frontend/eventPosters/EventPoster';
import { FallbackMusicPoster } from 'Shared/frontend/eventPosters/FallbackMusicPoster';
import { Overlay } from 'Shared/frontend/Overlay';
import { SpecialsPoster } from 'Shared/frontend/specialsPosters/SpecialsPoster';
import { createMemo,createResource,createSignal, For,Show, Switch } from 'solid-js';
import { SpecialsOverlayContent } from 'Shared/frontend/specialsPosters/SpecialsOverlayContent';
import { specialItemOverlayStyle } from 'Shared/frontend/menuPage/MenuPage';
import { theme, palette } from 'Shared/frontend/SettingsProvider';
import { Match } from 'solid-js';
import { posterGridStyle } from 'Shared/frontend/posterGridStyle';
import { StylesProvider, useStylePath, useStyleDetails } from 'Shared/frontend/widgets/StylesProvider';
import { useInsideLink } from 'Shared/frontend/widgets/InsideLinkContext';
import { displayState } from 'Shared/frontend/displayState';
import { BusinessHoursEntry } from 'Shared/common/BusinessHoursEntry';
import { emailTypes } from 'Shared/common/emailTypes';
import { useVenue } from 'Shared/VenueProvider';
import { SocialIcons } from 'Shared/frontend/footer/SocialIcons';
import { useServer } from 'Shared/SiteProvider';
import { Phone as PhoneIcon } from 'Shared/frontend/icons/Phone';
import { Pin } from 'Shared/frontend/icons/Pin';
import { sanitise } from 'Shared/common/Sanitise';
import { Poster } from 'Shared/frontend/Poster';


//XXX MAYBE remove event-specific look from subNavButton
//TODO hover

const scrollStyle = () => css(theme().widgets.scrollStyle,{
	paddingLeft: 10,
	paddingRight: 10,
	maxWidth: '100%',
	display: 'flex',
	flexWrap: 'nowrap',
	overflowX: 'auto',
	scrollbarColor: 'black #666363',
	justifyContent: 'flex-start',
	gap: 48
});	

const wrapStyle = {
//	marginTop: 24,
//	marginBottom: 24,
	paddingTop: 24,
	paddingBottom: 24,
};	

const specialOverlayBodyStyle = () => css({
	maxWidth: 396,					//XXX set anywhere?
	minHeight: 680,
	backgroundColor: palette().dimBackground,
	marginBottom: '3em',
});



export function Content(P:{type,carouselToScroll,styling})
{
	const venue = useVenue();

//XXX social icons are pre-styled...	

	return (
		<Switch>
			<Match when={P.type == 'music'}>
				<EventPosters {...P} />
			</Match>
			<Match when={P.type == 'nonMusic'}>
				<EventPosters {...P} />
			</Match>
			<Match when={P.type == 'events'}>
				<EventPosters {...P} />
			</Match>
			<Match when={P.type == 'pastEvents'}>
				<EventPosters {...P} />
			</Match>
			<Match when={P.type =='specials'}>
				<SpecialsPosters {...P} specials={specials()} type={P.specialsType} />
			</Match>
			<Match when={P.type == 'hours'}>
				<Hours styling={P.styling} />
			</Match>
			<Match when={P.type == 'emails'}>
				<Emails styling={P.styling} />
			</Match>
			<Match when={P.type == 'phone'}>
				<Phone styling={P.styling} />
			</Match>
			<Match when={P.type == 'socials'}>
				<SocialIcons venue={venue} styling={P.styling} />
			</Match>
			<Match when={P.type == 'address'}>
				<Address styling={P.styling} />
			</Match>
			<Match when={P.type == 'businessNumbers'}>
				<BusinessNumbers styling={P.styling} />
			</Match>
		</Switch>
	);
}

/*
	An anchor inside an anchor is invalid HTML and breaks hydration of the whole section, so inside a
	link block these render as plain wrappers and the surrounding link takes the click.
	cf InsideLinkContext.
*/
function ContentLink(P:{href:string,newWindow:boolean,class:string,children})
{
	const insideLink = useInsideLink();

	return (
		<Switch>
			<Match when={insideLink()}>
				<span class={P.class}>{P.children}</span>
			</Match>
			<Match when={true}>
				<a class={P.class} href={P.href} target={P.newWindow ? '_blank' : undefined}>{P.children}</a>
			</Match>
		</Switch>
	);
}

function ActionLink(P:{class:string,select:() => void,children})
{
	const insideLink = useInsideLink();

	return (
		<Switch>
			<Match when={insideLink()}>
				<span class={P.class}>{P.children}</span>
			</Match>
			<Match when={true}>
				<a class={P.class} href='' onClick={e => {e.preventDefault(); P.select();}}>{P.children}</a>
			</Match>
		</Switch>
	);
}

/*
	The unrendered fallback poster stands in whenever the poster we would otherwise show can't be
	trusted: out of date, or - for the new builder, which shows its rendered image - never rendered
	(a missing image on the design stops the render, leaving the poster stale).
	Music only: it draws a lineup. A non-music event whose builder poster can't be trusted falls
	through to the drawn poster instead.
*/
function needsFallbackPoster(event): boolean
{
	if (event.eventType != 'music' || event.imageType != 'auto')
		return false;

	if (usesRenderedPoster(event))
		return !hasCurrentRenderedPoster(event);

	return !event.poster?.isCurrent;
}

function EventPosters(P:{type:string,carouselToScroll,styling})
{
	const styleDetails = useStyleDetails();
	const stylePath = useStylePath();

	const [events] = createResource(async () => {
		switch(P.type) {
			case 'events':		return await useServer().futureEvents.query();
			case 'pastEvents':	return await useServer().pastEvents.query();
			case 'music':		return await useServer().musicEvents.query();
			case 'nonMusic':	return await useServer().nonMusicEvents.query();
		}
	});

	return (
		<Show when={(events()?.length ?? 0) > 0}>
			<div class={css(
				wrapStyle,
				events().length > P.carouselToScroll ? scrollStyle() : posterGridStyle(),
				P.styling.css
			)}>
				<For each={events()}>{(event,index) =>
					<StylesProvider path={[...stylePath,{key:index()}]} styleDetails={styleDetails}>
						<Poster theme={event.posterSettings?.theme}>
							<ContentLink class={css(theme().a)} href={`/event/${event.slug}`} newWindow={false}>
								<Switch>
									<Match when={needsFallbackPoster(event)}>
										<FallbackMusicPoster {...event} times={event.times} lineup={event.lineup} />
									</Match>
									<Match when={usesRenderedPoster(event) && hasCurrentRenderedPoster(event)}>
										<EventPosterImage event={event} />
									</Match>
									<Match when={true}>
										<EventPoster {...event} times={event.times} lineup={event.lineup} />
									</Match>
								</Switch>
							</ContentLink>
						</Poster>
					</StylesProvider>
				}</For>
			</div>
		</Show>
	);
}

function SpecialsPosters(P:{carouselToScroll,styling})
{
	const [specials] = createResource(() => useServer().specials.query({}) );

	const [current,setCurrent] = createSignal();

//FIXME in DifferentDrummer database replace specialsGroup with 'type'
//FIXME Townie (& Dock) needs 'general' type added to DB
//FIXME add different specials in theme()
//TODO add group selection to specials page. IN TIME better to allow special groups to be added like new menus.
	const items = createMemo(() => {
		const items = [];

		for (const special of specials() ?? [])
			if (special.type == P.type)
				items.push(special);
				
		return items;
	});

	const styleDetails = useStyleDetails();
	const stylePath = useStylePath();

	return (<>
		<Show when={items().length > 0}>
			<div class={css(
				wrapStyle,
				items().length > P.carouselToScroll ? scrollStyle() : posterGridStyle(),
				P.styling.css
			)}>
				<For each={items()}>{(special,index) => 
					<StylesProvider path={[...stylePath,{key:index()}]} styleDetails={styleDetails} > 
						<ActionLink class={css(theme().a)} select={() => setCurrent(special)}>
							<Poster theme={special.posterSettings?.theme} >
								<SpecialsPoster {...special} times={special.times} />
							</Poster>
						</ActionLink>
					</StylesProvider>
				}</For>
			</div>
		</Show>

		<Show when={current()}>
			<Overlay iconOverImage={true} animation='slideIn' close={() => setCurrent(undefined)}>
				<div class={specialItemOverlayStyle()}>
					<div class={specialOverlayBodyStyle()}>
						<SpecialsOverlayContent special={current()} times={current().times} />
					</div>
				</div>
			</Overlay>
		</Show>
	</>);
}

function Hours(P:{styling})
{
//XXX do I need to free up BusinessHoursEntry?

	const venue = useVenue();

	return (
		<div class={css(P.styling?.css)}>
			<For each={venue.operatingHours}>{ hours =>
				<div>
					<BusinessHoursEntry {...hours} />
				</div>
			}</For>
		</div>
	);
}

function Emails(P:{styling})
{
//XXX cf a table and a row-based versions... Nice if the style could tell us which format needs to be supplied.
	const venue = useVenue();

	return (
		<div class={css(theme().text.plain, P.styling?.css)}>
			<For each={venue.email}>{ email =>
				<Show when={!email.hide}>
					<div>
						{email.type!='custom' ? emailTypes[email.type] : email.customLabel}

						<Show when={email.tips}>
							{' '}
							({email.tips})
						</Show>
						:
						{' '}
						{email.email}
					</div>
				</Show>
			}</For>
		</div>
	);
}

//TODO map	
//TODO ABN? ACN? or put in Text component
//TODO third party services


/* PHILOPSOPY: 
	Here I'm using more of a 'styled component' approach... relying mostly on settings 
	and/or select from a range of templates.
	Use the other 'style' approach doesn't accommodate the icons or new line very well 
	and seems very fussy - relying on class names, removing blocks, etc.
*/

function Address(P:{styling})
{
	const venue = useVenue();

	//TODO share styles with Phone
	const aStyle = () => css(theme().a, theme().text.plain, {
		color: palette().text,
		display: 'flex'
	}, P.styling?.css);

	const iconStyle = css({
		width: '1.9em',
		marginRight: '0.35em',

		svg: {
			fill: palette().text
		}
	});

	const addressStyle = css(sanitise.address, theme().text.plain, {
		color: palette().text
	});

	return (
		 <ContentLink class={aStyle()} href={venue.mapUrl} newWindow={true}>
			<div class={iconStyle}>
				<Pin />
			</div>
			<address class={addressStyle}>
				{venue.streetAddress}
				<br/>
				{venue.suburb}{' '}
				{displayState(venue.state)}{' '}
				{venue.postcode}
			</address>
		</ContentLink>
	);
}

function Phone(P:{styling})
{
	const venue = useVenue();

	const aStyle = () => css(theme().a, theme().text.plain, {
		color: palette().text,
		display: 'flex'
	}, P.styling?.css);

	const iconStyle = css({
		width: '1.9em',
		marginRight: '0.35em',

		svg: {
			fill: palette().text
		}
	});

	return (
		<ContentLink class={aStyle()} href={`tel:${venue.phoneNumber}`} newWindow={false}>
			<div class={iconStyle}>
				<PhoneIcon />
			</div>
			{venue.phoneNumber}
		</ContentLink>
	);
}

function BusinessNumbers(P:{styling})
{
	const businessNumbersStyle = () => css(theme().definitions, theme().text.plain, P.styling?.css);

	const venue = useVenue();

	return (
		<div class={businessNumbersStyle()}>
			<Show when={venue.abn}>
				ABN: {venue.abn}
			</Show>
			<Show when={venue.acn}>
				ACN: {venue.acn}
			</Show>
		</div>
	);
}

